import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles, Mail, Loader2, LockKeyhole, ChartNoAxesColumnIncreasing, Zap } from 'lucide-react';
import { api } from '../context/AuthContext';
import { QUESTIONS, ENGINES } from '../mock/mock';
import ConstellationBackdrop from '../components/ConstellationBackdrop';
import ArclipseBrandLockup from '../components/ArclipseBrandLockup';

function nowMs() {
  return (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
}

function useQuestionTimer(active, resetKey) {
  const startRef = useRef(0);
  const [displaySeconds, setDisplaySeconds] = useState(0);

  useEffect(() => {
    if (!active) { startRef.current = 0; setDisplaySeconds(0); return; }
    startRef.current = nowMs();
    setDisplaySeconds(0);
    const id = setInterval(() => {
      const start = startRef.current || nowMs();
      setDisplaySeconds(Math.floor((nowMs() - start) / 1000));
    }, 250);
    return () => clearInterval(id);
  }, [active, resetKey]);

  const getElapsedSeconds = useCallback(() => {
    const start = startRef.current;
    if (!start) return 0;
    return (nowMs() - start) / 1000;
  }, []);

  return { displaySeconds, getElapsedSeconds };
}

function computeLocalEngineSignal(answers) {
  if (!answers.length) return null;
  const counts = answers.reduce((acc, answer) => {
    acc[answer.engine] = (acc[answer.engine] || 0) + 1;
    return acc;
  }, {});
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  return { top: sorted[0]?.[0] || null };
}

function QuizHeader({ step, total, second, onExit }) {
  return (
    <div className="flex items-center justify-between max-w-[1100px] mx-auto w-full pb-4 border-b border-white/8 mb-8">
      <div className="flex items-center gap-3 md:gap-5">
        <ArclipseBrandLockup size="sm" />
        <span className="hidden md:inline text-white/30">|</span>
        <span className="hidden md:inline font-data text-[11px] tracking-[0.18em] uppercase text-white/55">
          Question {step + 1} / {total}
        </span>
        <SignalLabel text={second ? 'Deep signal' : 'Instinct round'} compact />
      </div>
      <button onClick={onExit} className="text-[10px] tracking-[0.22em] uppercase text-white/40 hover:text-[#e05252] border border-white/10 hover:border-[#e05252]/40 px-3 py-1.5">
        Exit
      </button>
    </div>
  );
}

function SignalLabel({ text, compact = false }) {
  return (
    <div className={`inline-flex items-center justify-center gap-3 text-white/70 ${compact ? 'text-[10px]' : 'text-[11px]'} font-bold tracking-[0.22em] uppercase`}>
      <span
        aria-hidden="true"
        className="block h-1.5 w-1.5 rounded-full bg-white"
        style={{ boxShadow: '0 0 0 4px rgba(255,255,255,0.11), 0 0 18px rgba(255,255,255,0.65)' }}
      />
      <span>{text}</span>
    </div>
  );
}

export default function Assessment() {
  const navigate = useNavigate();

  // Flow: email gate -> rules -> quiz -> midpoint -> analyzing -> results.
  const [phase, setPhase] = useState('email_gate');
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [pickedIdx, setPickedIdx] = useState(null);

  // Email gate state
  const [emailVal, setEmailVal] = useState('');
  const [emailErr, setEmailErr] = useState(null);
  const [quizErr, setQuizErr] = useState(null);
  const [emailBusy, setEmailBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  const { displaySeconds: elapsed, getElapsedSeconds } = useQuestionTimer(phase === 'quiz', step);
  const total = QUESTIONS.length;
  const q = QUESTIONS[step];
  const second = step >= 30;

  // Step A: submit email and create the saved review session.
  const submitEmailGate = async (e) => {
    e?.preventDefault();
    const trimmed = emailVal.trim();
    if (!/^\S+@\S+\.\S+$/.test(trimmed)) {
      setEmailErr('Enter a valid email address to continue.');
      return;
    }
    setEmailBusy(true);
    setEmailErr(null);
    try {
      const r = await api.post('/quiz/session', { email: trimmed });
      setSessionId(r.data.session_id);
      setEmailBusy(false);
      setPhase('rules');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message = typeof detail === 'object' ? detail.message : detail;
      setEmailErr(message || 'We could not create your saved review session. Please try again.');
      setEmailBusy(false);
    }
  };

  // Step B: record each answer and save progress.
  const pick = (idx) => {
    if (pickedIdx !== null) return;
    setQuizErr(null);
    setPickedIdx(idx);

    const opt = q.options[idx];
    const timeTakenSec = getElapsedSeconds();
    const entry = {
      questionId: q.id,
      engine: opt.engine,
      optionIndex: idx,
      timeTakenSec,
      timeTaken: Math.floor(timeTakenSec),
    };

    let nextAnswersSnapshot = null;
    setAnswers(prev => {
      const next = [...prev];
      const existing = next.findIndex(a => a.questionId === q.id);
      if (existing >= 0) next[existing] = entry; else next.push(entry);
      nextAnswersSnapshot = next;
      return next;
    });

    // Fire-and-forget progressive save.
    if (sessionId) {
      api.put('/quiz/answer', {
        session_id: sessionId,
        question_id: q.id,
        engine: opt.engine,
        option_index: idx,
        time_taken_sec: timeTakenSec,
      }).catch(() => {});
    }

    setTimeout(() => {
      setPickedIdx(null);
      if (step + 1 === 30) { setPhase('midpoint'); return; }
      if (step + 1 >= total) { goToResults(nextAnswersSnapshot); return; }
      setStep(s => s + 1);
    }, 320);
  };

  const continueDeep = () => { setPhase('quiz'); setStep(30); };

  // Step C: finalize and navigate to results.
  const goToResults = useCallback(async (answersOverride = null) => {
    setPhase('analyzing');
    // Use a closure snapshot because the answer state update may not have flushed yet.
    const currentAnswers = answersOverride || answers;
    try {
      let r;
      if (sessionId) {
        const completed = await api.post('/quiz/complete', {
          session_id: sessionId,
          email: emailVal.trim() || null,
          answers: currentAnswers,
        });
        r = completed.data.result;
      }
      if (!r) throw new Error('No result returned');
      // Navigate after the analyzing delay.
      setTimeout(() => {
        navigate('/results', {
          state: {
            result: r,
            email: emailVal.trim(),
            sessionId,
          },
        });
      }, 1800);
    } catch {
      setQuizErr('We could not save your final ARC signal. Please check your connection and try the last question again.');
      setPhase('quiz');
    }
  }, [answers, sessionId, emailVal, navigate]);

  const exit = () => {
    if (window.confirm('Exit the ARC signal? Your progress will be lost.')) navigate('/');
  };

  const partial = useMemo(() => computeLocalEngineSignal(answers), [answers]);
  const eng = partial ? ENGINES[partial.top] : null;

  return (
    <div className="relative min-h-screen font-body text-white bg-[#050403] overflow-hidden">
      <ConstellationBackdrop />
      <div className="relative z-10 px-5 md:px-10 py-6">

        {/* Email gate */}
        {phase === 'email_gate' && (
          <div className="max-w-[520px] mx-auto fade-up pt-12 md:pt-20">
            {/* Nav */}
            <div className="flex items-center justify-between pb-4 border-b border-white/8 mb-10">
              <ArclipseBrandLockup />
              <button onClick={() => navigate('/')} className="eyebrow text-white/45 hover:text-white border border-white/10 hover:border-white/30 px-3 py-1.5">
                Back
              </button>
            </div>

            <div className="text-center">
              <SignalLabel text="Founder signal" />
              <h1 className="mt-5 font-head text-[30px] md:text-[36px] leading-[1.2]" style={{ color: '#ffffff' }}>
                Complete your ARC signal.
              </h1>
              <p className="mt-3 text-white/55 text-[14px] leading-[1.8]">
                ARC is a second-stage founder diagnostic. Complete it after Arclipse Constellation invites you to add deeper operating context to your application.
              </p>
            </div>

            {/* Lock-in value props */}
            <div className="mt-6 space-y-2">
              {[
                { icon: LockKeyhole, text: 'Your email connects the result to your founder review' },
                { icon: ChartNoAxesColumnIncreasing, text: 'Your Engine and Arc add operating context beyond the pitch deck' },
                { icon: Zap, text: 'The result supports review; it does not decide the outcome alone' },
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-3 px-4 py-2.5 rounded-lg"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <Icon size={14} style={{ color: '#e4e4e8', flexShrink: 0 }} />
                  <span className="text-[13px] text-white/60 leading-snug">{text}</span>
                </div>
              ))}
            </div>

            <form onSubmit={submitEmailGate} className="mt-6 space-y-3">
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  className="input pl-9"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={emailVal}
                  disabled={emailBusy}
                  onChange={e => { setEmailVal(e.target.value); setEmailErr(null); }}
                />
              </div>
              {emailErr && <p className="text-[12.5px]" style={{ color: '#e05252' }}>{emailErr}</p>}
              <button
                type="submit"
                disabled={emailBusy}
                className="constellation-cta constellation-cta--full"
              >
                {emailBusy ? <><Loader2 size={14} className="animate-spin" /> Creating session</> : <>Begin ARC signal <ArrowRight size={14} /></>}
              </button>
            </form>
            {emailBusy && (
              <p className="mt-3 text-center text-[12px] text-white/40">
                Creating your saved review session.
              </p>
            )}
            <p className="mt-4 text-center text-[12px] text-white/30">
              No spam. No credit card. Your ARC signal is linked to this email.
            </p>
          </div>
        )}

        {/* Rules */}
        {phase === 'rules' && (
          <div className="max-w-[860px] mx-auto fade-up pt-6">
            <div className="flex items-center justify-between pb-4 border-b border-white/8 mb-10">
              <ArclipseBrandLockup />
              <button onClick={() => navigate('/')} className="eyebrow text-white/45 hover:text-white border border-white/10 hover:border-white/30 px-3 py-1.5">
                Back
              </button>
            </div>

            <div className="text-center mb-10">
              <SignalLabel text="Before you begin" />
              <h1 className="mt-5 font-head text-[36px] md:text-[48px] leading-[1.1]" style={{ color: '#ffffff' }}>
                How the ARC signal works.
              </h1>
              <p className="mt-4 text-white/65 max-w-[640px] mx-auto text-[15px] leading-[1.85]">
                This is practical operating context, not a personality test. Answer from instinct, especially when the question creates friction.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              {[
                { n: '01', t: 'Trust your first instinct.', b: 'Your fastest answer often shows your real operating pattern before overthinking edits it.' },
                { n: '02', t: 'Time itself is a signal.', b: 'Fast answers carry more weight. Slow answers still count, but hesitation can reveal conflict.' },
                { n: '03', t: 'Every answer maps to an Engine.', b: 'Each choice points toward one of six Engines: Titan, Arbiter, Alchemist, Artisan, Oracle, or Sovereign.' },
                { n: '04', t: 'Two Engines create your Arc.', b: 'Your strongest two Engines combine into one of 30 Arcs. That Arc becomes part of your Cohort 1 review signal.' },
              ].map(r => (
                <div key={r.n} className="panel p-5">
                  <div className="font-head text-[28px] font-bold leading-none" style={{ color: 'rgba(255,255,255,0.18)' }}>{r.n}</div>
                  <div className="mt-3 font-head text-[16px] text-white">{r.t}</div>
                  <div className="mt-1 text-[14px] leading-[1.7] text-white/60">{r.b}</div>
                </div>
              ))}
            </div>

            <div className="mt-8 panel-strong p-5">
              <div className="text-[13px] text-white/75 leading-[1.8]">
                <span className="font-head text-white/80">Format:</span> 60 questions, about 7 minutes.{' '}
                The result shows how you handle stress, build, decide, lead, sell, create, and respond when pressure rises.
              </div>
            </div>

            <div className="mt-8 flex justify-center">
              <button
                onClick={() => setPhase('quiz')}
                className="constellation-cta constellation-cta--wide"
              >
                Begin <ArrowRight size={14} />
              </button>
            </div>
          </div>
        )}

        {/* Quiz header */}
        {phase === 'quiz' && (
          <QuizHeader step={step} total={total} second={second} onExit={exit} />
        )}

        {/* Question */}
        {phase === 'quiz' && (
          <div className="max-w-[820px] mx-auto fade-up">
            <div className="flex items-center gap-3 mb-5">
              <div className="flex-1 h-[3px] bg-white/6 rounded-full overflow-hidden">
                <div className="h-full transition-[width] duration-500" style={{ width: `${(step / total) * 100}%`, background: '#e4e4e8' }} />
              </div>
              <div className="font-data text-[11px] tracking-[0.18em] uppercase" style={{ color: elapsed < 10 ? '#1D9E75' : elapsed > 25 ? '#e05252' : 'rgba(255,255,255,0.55)' }}>
                {elapsed}s
              </div>
            </div>

            <div className="panel-strong p-7 md:p-10">
              <div className="text-[11px] tracking-[0.32em] uppercase font-bold mb-4" style={{ color: 'rgba(255,255,255,0.45)' }}>{q.theme}</div>
              <h2 className="font-head text-[22px] md:text-[28px] leading-[1.35] text-white">{q.scenario}</h2>
              {quizErr && <div className="mt-4 text-[12.5px]" style={{ color: '#e05252' }}>{quizErr}</div>}
              <div className="mt-7 grid gap-3">
                {q.options.map((o, idx) => {
                  const active = pickedIdx === idx;
                  return (
                    <button
                      key={idx}
                      onClick={() => pick(idx)}
                      className="text-left px-5 py-4 rounded-lg border transition-colors"
                      style={{
                        background: active ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.025)',
                        borderColor: active ? '#e4e4e8' : 'rgba(255,255,255,0.06)',
                        color: '#fff',
                      }}
                    >
                      <div className="flex items-start gap-4">
                        <span className="mt-1 inline-block h-4 w-4 rounded-full border shrink-0" style={{ borderColor: active ? '#e4e4e8' : 'rgba(255,255,255,0.25)', background: active ? '#e4e4e8' : 'transparent' }} />
                        <span className="text-[14.5px] leading-[1.65] text-white/85">{o.text}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Midpoint */}
        {phase === 'midpoint' && partial && (
          <div className="max-w-[820px] mx-auto fade-up text-center pt-12 md:pt-20">
            <SignalLabel text="Arc forming" />
            <div className="relative mx-auto mt-7 h-[150px] w-[150px]">
              <div className="absolute inset-0 rounded-full" style={{ border: '1px solid rgba(255,255,255,0.34)', animation: 'arcSpin 18s linear infinite' }} />
              <div className="absolute inset-5 rounded-full" style={{ border: '1px solid rgba(255,255,255,0.20)', animation: 'arcSpin 12s linear infinite reverse' }} />
              <div className="absolute left-1/2 top-1/2 h-16 w-16 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ background: 'rgba(255,255,255,0.12)', boxShadow: '0 0 42px rgba(255,255,255,0.24)' }} />
            </div>
            <h1 className="mt-6 font-head text-[34px] md:text-[44px] leading-[1.1]" style={{ color: '#ffffff' }}>
              Your primary Engine is forming.
            </h1>
            <div className="mt-2 font-data text-[12px] tracking-[0.18em] uppercase text-white/45">
              Halfway signal: {eng?.name || 'Engine'} pattern detected
            </div>
            <p className="mt-5 max-w-[600px] mx-auto text-white/65 text-[15px] leading-[1.85]">
              The ARC signal has enough data to see the outline, but not enough to assign your Arc. Continue to 60 questions to complete your Cohort 1 signal.
            </p>
            <div className="mt-7 flex justify-center">
              <button className="btn btn-primary" onClick={continueDeep}>
                Continue to 60 <ArrowRight size={14} />
              </button>
            </div>
          </div>
        )}

        {/* Analyzing */}
        {phase === 'analyzing' && (
          <div className="min-h-[calc(100vh-140px)] max-w-[600px] mx-auto fade-up text-center flex flex-col items-center justify-center py-12">
            {/* Dual concentric ring spinner */}
            <div style={{ position: 'relative', width: 72, height: 72, margin: '0 auto 28px' }}>
              <div style={{
                position: 'absolute', inset: 0,
                border: '2px solid rgba(255,255,255,0.15)',
                borderTopColor: '#e4e4e8',
                borderRadius: '50%',
                animation: 'arcSpin 1s linear infinite',
              }} />
              <div style={{
                position: 'absolute', inset: 12,
                border: '2px solid rgba(255,255,255,0.10)',
                borderRightColor: '#e4e4e8',
                borderRadius: '50%',
                animation: 'arcSpin 1.4s linear infinite reverse',
              }} />
              <div style={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%,-50%)',
                width: 8, height: 8, borderRadius: '50%',
                background: '#e4e4e8',
                boxShadow: '0 0 14px rgba(255,255,255,0.72)',
                animation: 'arcPulse 1.4s ease-in-out infinite',
              }} />
            </div>
            <style>{`
              @keyframes arcSpin { to { transform: rotate(360deg); } }
              @keyframes arcPulse {
                0%, 100% { transform: translate(-50%,-50%) scale(1); opacity: 1; }
                50%      { transform: translate(-50%,-50%) scale(1.6); opacity: 0.5; }
              }
              @keyframes calibLine {
                0%   { opacity: 0; transform: translateX(-6px); }
                100% { opacity: 1; transform: translateX(0); }
              }
            `}</style>
            <div className="flex items-center justify-center gap-2 text-white/80">
              <Sparkles size={18} style={{ color: '#e4e4e8' }} className="pulse-soft" />
              <span className="font-head text-[20px]">Calibrating your Arc...</span>
            </div>
            <div className="mt-6 space-y-2 font-data text-[12px] tracking-[0.16em] text-white/55">
              <div style={{ animation: 'calibLine 0.4s ease-out 0.2s backwards' }}>Reading Engine patterns</div>
              <div style={{ animation: 'calibLine 0.4s ease-out 0.7s backwards' }}>Weighing instinct and hesitation</div>
              <div style={{ animation: 'calibLine 0.4s ease-out 1.2s backwards' }}>Building your Arc result</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
